// Fenster in der Mitte des Bildschirms (auf dem Handy als Blatt von unten).
//
// Für Details, die man aus einer langen Liste heraus öffnet: früher erschienen
// sie als Karte oben auf der Seite — wer weit unten eine Rechnung anklickte,
// musste erst ganz nach oben scrollen. Das Fenster erscheint dort, wo man ist.
//
// Schließen: Knopf oben rechts, Esc, Klick daneben.
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import Icon from './Icon';
import { useSprache } from '../lib/i18n';

export function Fenster({ titel, unterzeile, aktionen, beiSchliessen, breite = 'sm:max-w-3xl', children }: {
  titel: ReactNode;
  unterzeile?: ReactNode;
  /** Knöpfe/Links im Kopf, links vom Schließen-Knopf. */
  aktionen?: ReactNode;
  beiSchliessen: () => void;
  breite?: string;
  children: ReactNode;
}) {
  const { t } = useSprache();
  const kennung = useId();
  const kasten = useRef<HTMLDivElement>(null);
  const schliessen = useRef(beiSchliessen);
  schliessen.current = beiSchliessen;

  useEffect(() => {
    const vorher = document.activeElement as HTMLElement | null;
    const ueberlauf = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    kasten.current?.focus();
    const taste = (e: KeyboardEvent) => { if (e.key === 'Escape') schliessen.current(); };
    window.addEventListener('keydown', taste);
    return () => {
      window.removeEventListener('keydown', taste);
      document.body.style.overflow = ueberlauf;
      vorher?.focus?.();
    };
  }, []);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/60 backdrop-blur-sm sm:items-center sm:p-4"
         onMouseDown={e => { if (e.target === e.currentTarget) beiSchliessen(); }}>
      <div ref={kasten} role="dialog" aria-modal="true" aria-labelledby={kennung} tabIndex={-1}
           className={`flex max-h-[92vh] w-full ${breite} flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl outline-none sm:rounded-2xl dark:bg-slate-900`}
           style={{ paddingBottom: 'var(--sicher-unten, 0px)' }}>
        <div className="flex items-start gap-3 border-b border-slate-200 px-5 py-4 dark:border-slate-800">
          <div className="min-w-0 flex-1">
            <h2 id={kennung} className="text-lg font-semibold text-slate-900 dark:text-slate-100">{titel}</h2>
            {unterzeile && <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">{unterzeile}</p>}
          </div>
          {aktionen && <div className="flex shrink-0 items-center gap-1">{aktionen}</div>}
          <button type="button" onClick={beiSchliessen} aria-label={t('aktion.schliessen')}
                  className="-me-1 shrink-0 rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-slate-100">
            <Icon name="schliessen" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
